/**
 * VerifiedProjectsPage — premium directory of verified token projects.
 *
 * Public view: hero, KPI strip, chain pills, spotlight, search/sort, grid.
 * Admin view: same plus a pending-approvals tab.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Shield, CheckCircle, Loader2, Plus, X, Search,
  Globe, Twitter, Clock, Sparkles, Calendar, TrendingUp, Award,
  Link as LinkIcon, ChevronLeft, ChevronRight, Trash2,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  getVerifiedProjects, applyForProjectVerification,
  getPendingProjects, approveProject, rejectProject, deleteProject,
} from '../services/api';

const CHAINS = [
  { id: 'ethereum', name: 'Ethereum', icon: '⟠', color: 'from-indigo-500/20 to-blue-500/20 border-indigo-400/30 text-indigo-300' },
  { id: 'solana',   name: 'Solana',   icon: '◎', color: 'from-purple-500/20 to-fuchsia-500/20 border-purple-400/30 text-purple-300' },
  { id: 'base',     name: 'Base',     icon: '🔵', color: 'from-blue-500/20 to-cyan-500/20 border-blue-400/30 text-blue-300' },
  { id: 'bsc',      name: 'BSC',      icon: '🟡', color: 'from-amber-500/20 to-yellow-500/20 border-amber-400/30 text-amber-300' },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷', color: 'from-cyan-500/20 to-sky-500/20 border-cyan-400/30 text-cyan-300' },
  { id: 'polygon',  name: 'Polygon',  icon: '🟣', color: 'from-violet-500/20 to-purple-500/20 border-violet-400/30 text-violet-300' },
];

const SORTS = [
  { id: 'recent',       label: 'Most Recent' },
  { id: 'alphabetical', label: 'A → Z' },
  { id: 'active',       label: 'Most Active' },
];

const PER_PAGE = 40;

export default function VerifiedProjectsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.email === 'admin@haythix.local' || user?.email === 'sertec@sertecint.com';

  const [tab, setTab] = useState('verified');
  const [projects, setProjects] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  // Independent mount-only timer that ALWAYS hides the inline loader after
  // 5 seconds. Decoupled from the fetch effect so it cannot be cancelled by
  // dep-array re-runs. After this fires, the page shows real data or the
  // natural empty state — never a perpetual spinner.
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 5000);
    return () => clearTimeout(t);
  }, []);
  const [showApply, setShowApply] = useState(false);
  const [search, setSearch] = useState('');
  const [chainFilter, setChainFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Apply form state
  const [form, setForm] = useState({ tokenSymbol: '', tokenAddress: '', chain: 'ethereum', projectName: '', description: '', websiteUrl: '', twitterHandle: '', discordUrl: '', telegramUrl: '', linkedinUrl: '', githubUrl: '', auditUrl: '', teamSize: '', launchDate: '', logo: null });
  const [applying, setApplying] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const uid = user?.userId;

  useEffect(() => {
    if (!uid) return;
    // No cancelled flag — see CommunityProfilePage for why. Late setStates
    // on unmounted components are handled gracefully by React 18.
    (async () => {
      try {
        const v = await getVerifiedProjects();
        setProjects(v?.projects || []);
      } catch {}
      if (isAdmin) {
        try {
          const p = await getPendingProjects();
          setPending(p?.projects || []);
        } catch {}
      }
      setLoading(false);
    })();
  }, [uid, isAdmin]);

  function showToast(msg, ms = 3000) {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  }

  async function refreshVerified() {
    try { const v = await getVerifiedProjects(); setProjects(v?.projects || []); } catch {}
  }

  async function handleApply() {
    if (!form.tokenSymbol || !form.tokenAddress || !form.projectName) {
      showToast('Please fill in all required fields');
      return;
    }
    setApplying(true);
    let ok = false;
    try {
      await applyForProjectVerification(form);
      ok = true;
    } catch (err) {
      showToast(`❌ ${err.message || 'Failed to submit. Please try again.'}`, 5000);
    }
    setApplying(false);
    if (ok) {
      setShowApply(false);
      setForm({ tokenSymbol: '', tokenAddress: '', chain: 'ethereum', projectName: '', description: '', websiteUrl: '', twitterHandle: '', discordUrl: '', telegramUrl: '', linkedinUrl: '', githubUrl: '', auditUrl: '', teamSize: '', launchDate: '', logo: null });
      setLogoPreview(null);
      showToast("✅ Application submitted! We'll review within 48 hours.", 5000);
      refreshVerified();
    }
  }

  async function handleApprove(projectId) {
    try {
      await approveProject(projectId);
      setPending(prev => prev.filter(p => p.projectId !== projectId));
      showToast('Project approved!');
      refreshVerified();
    } catch {}
  }

  async function handleReject(projectId) {
    try {
      await rejectProject(projectId, 'Does not meet verification criteria at this time.');
      setPending(prev => prev.filter(p => p.projectId !== projectId));
      showToast('Project rejected.');
    } catch {}
  }

  // ─── KPIs and filtering ──────────────────────────────────────────────
  const chainCounts = useMemo(() => {
    const counts = { all: projects.length };
    CHAINS.forEach(c => { counts[c.id] = projects.filter(p => p.chain === c.id).length; });
    return counts;
  }, [projects]);

  const newThisMonth = useMemo(() => {
    const cutoff = Date.now() - 30 * 86400000;
    return projects.filter(p => new Date(p.verifiedAt || 0).getTime() > cutoff).length;
  }, [projects]);

  const totalCampaigns = useMemo(
    () => projects.reduce((sum, p) => sum + (p.activeCampaigns || 0), 0),
    [projects]
  );

  const chainsRepresented = useMemo(
    () => new Set(projects.map(p => p.chain).filter(Boolean)).size,
    [projects]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = projects;
    if (chainFilter !== 'all') list = list.filter(p => p.chain === chainFilter);
    if (q) list = list.filter(p =>
      p.projectName?.toLowerCase().includes(q) ||
      p.tokenSymbol?.toLowerCase().includes(q)
    );
    list = [...list];
    if (sortBy === 'recent') {
      list.sort((a, b) => new Date(b.verifiedAt || b.appliedAt || 0) - new Date(a.verifiedAt || a.appliedAt || 0));
    } else if (sortBy === 'alphabetical') {
      list.sort((a, b) => (a.projectName || '').localeCompare(b.projectName || ''));
    } else if (sortBy === 'active') {
      list.sort((a, b) => (b.activeCampaigns || 0) - (a.activeCampaigns || 0));
    }
    return list;
  }, [projects, search, chainFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // Spotlight: 3 most-recently-verified, but only on first page with no filters
  const showSpotlight = chainFilter === 'all' && !search.trim() && safePage === 1 && sortBy === 'recent';
  const spotlight = showSpotlight ? filtered.slice(0, 3) : [];
  const restAfterSpotlight = showSpotlight ? filtered.slice(3) : filtered;
  const pagedNonSpotlight = showSpotlight
    ? restAfterSpotlight.slice(0, PER_PAGE - 3)
    : paged;

  // We deliberately do NOT block the whole page on a loading guard. A single
  // hung request shouldn't trap the user on a spinner — the shell renders
  // immediately and individual sections show inline loading where relevant.

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Premium Hero ────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/30">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Verified Projects
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </h1>
              <p className="text-sm text-gray-400 mt-0.5 max-w-2xl">
                Token projects manually reviewed by the haythix team. Identity, contract,
                and team confirmed before listing.
              </p>
            </div>
          </div>
          <button onClick={() => setShowApply(true)}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold flex items-center gap-2 shadow-lg shadow-amber-500/30 transition-all hover:-translate-y-0.5">
            <Plus className="w-4 h-4" /> Apply for Verification
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <KPI icon={Award}      label="Total Verified"   value={projects.length}     color="amber" />
          <KPI icon={Sparkles}   label="New This Month"   value={newThisMonth}        color="emerald" />
          <KPI icon={LinkIcon}   label="Chains Covered"   value={chainsRepresented}   color="violet" />
          <KPI icon={TrendingUp} label="Active Campaigns" value={totalCampaigns}      color="cyan" />
        </div>
      </div>

      {/* ─── Tabs (admin) ────────────────────────────────────────────── */}
      {isAdmin && (
        <div className="flex gap-2">
          <button onClick={() => setTab('verified')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
              tab === 'verified'
                ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                : 'bg-dark-800 text-gray-400 border-dark-700/50 hover:text-white'
            }`}>
            <CheckCircle className="w-3.5 h-3.5 inline mr-1.5" /> Verified ({projects.length})
          </button>
          <button onClick={() => setTab('pending')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border relative ${
              tab === 'pending'
                ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                : 'bg-dark-800 text-gray-400 border-dark-700/50 hover:text-white'
            }`}>
            <Clock className="w-3.5 h-3.5 inline mr-1.5" /> Pending ({pending.length})
            {pending.length > 0 && tab !== 'pending' && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center">
                {pending.length}
              </span>
            )}
          </button>
        </div>
      )}

      {tab === 'verified' && (
        <>
          {/* ─── Chain filter pills + search/sort ─────────────────────── */}
          <div className="space-y-3">
            <div className="flex gap-2 overflow-x-auto pb-1">
              <ChainPill active={chainFilter === 'all'} onClick={() => { setChainFilter('all'); setPage(1); }}
                icon="🌐" name="All chains" count={chainCounts.all} />
              {CHAINS.map(c => (
                <ChainPill key={c.id} active={chainFilter === c.id} onClick={() => { setChainFilter(c.id); setPage(1); }}
                  icon={c.icon} name={c.name} count={chainCounts[c.id] || 0} activeColor={c.color} />
              ))}
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search by project name or token symbol..."
                  className="w-full bg-dark-800 border border-dark-700/50 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:border-amber-400 focus:outline-none" />
              </div>
              <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
                className="bg-dark-800 border border-dark-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none">
                {SORTS.map(s => <option key={s.id} value={s.id}>Sort: {s.label}</option>)}
              </select>
            </div>
          </div>

          {/* ─── Spotlight (recently verified, top 3) ─────────────────── */}
          {spotlight.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Recently Verified
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {spotlight.map(p => (
                  <SpotlightCard key={p.projectId} project={p} onClick={() => setSelectedProject(p)} />
                ))}
              </div>
            </div>
          )}

          {/* ─── Full grid (or inline loader / empty state) ──────────── */}
          {showLoader && projects.length === 0 ? (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
              <span className="text-sm text-gray-400">Loading verified projects…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
              <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-white font-bold mb-2">
                {search.trim() || chainFilter !== 'all' ? 'No matches' : 'No verified projects yet'}
              </h3>
              <p className="text-gray-500 text-sm mb-4">
                {search.trim() || chainFilter !== 'all'
                  ? 'Try a different search term or chain filter.'
                  : 'Be the first to get your token project verified on haythix.'}
              </p>
              {!search.trim() && chainFilter === 'all' && (
                <button onClick={() => setShowApply(true)}
                  className="px-4 py-2 rounded-xl bg-amber-500 text-dark-900 text-sm font-bold inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Apply for Verification
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {pagedNonSpotlight.map(project => (
                <ProjectCard key={project.projectId} project={project}
                  onClick={() => setSelectedProject(project)}
                  isAdmin={isAdmin}
                  onDelete={async () => {
                    if (!confirm('Delete this project?')) return;
                    try {
                      await deleteProject(project.projectId);
                      setProjects(prev => prev.filter(p => p.projectId !== project.projectId));
                      showToast('Project deleted');
                    } catch {}
                  }} />
              ))}
            </div>
          )}

          {/* ─── Pagination ─────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-dark-800 text-gray-400 hover:text-white disabled:opacity-30 border border-dark-700/50 flex items-center gap-1">
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <span className="text-xs text-gray-500">Page {safePage} of {totalPages} · {filtered.length} projects</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-dark-800 text-gray-400 hover:text-white disabled:opacity-30 border border-dark-700/50 flex items-center gap-1">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* ─── Trust signals ──────────────────────────────────────────── */}
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-6">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-400" /> What verification means
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: CheckCircle, title: 'Identity reviewed',  desc: 'Team identity and project ownership confirmed by the haythix review team.' },
                { icon: Award,       title: 'Contract reviewed',  desc: 'Token contract checked for honeypots, owner backdoors, and basic safety patterns.' },
                { icon: Sparkles,    title: 'Transparency',       desc: 'Active marketing campaigns are disclosed on the project profile.' },
              ].map((s, i) => (
                <div key={i} className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
                  <s.icon className="w-5 h-5 text-emerald-400 mb-2" />
                  <p className="text-sm font-bold text-white">{s.title}</p>
                  <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-4 text-center">
              Verification confirms identity and basic safety — not financial advice or endorsement.
            </p>
          </div>
        </>
      )}

      {/* ─── Pending Applications (Admin only) ──────────────────────── */}
      {tab === 'pending' && isAdmin && (
        pending.length === 0 ? (
          <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
            <Clock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <h3 className="text-white font-bold">No pending applications</h3>
            <p className="text-gray-500 text-sm mt-1">All caught up — new submissions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map(project => (
              <PendingApplicationCard key={project.projectId} project={project}
                onApprove={() => handleApprove(project.projectId)}
                onReject={() => handleReject(project.projectId)} />
            ))}
          </div>
        )
      )}

      {/* ─── Project detail modal ─────────────────────────────────────── */}
      {selectedProject && (
        <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}

      {/* ─── Apply modal ──────────────────────────────────────────────── */}
      {showApply && (
        <ApplyModal
          form={form} setForm={setForm}
          logoPreview={logoPreview} setLogoPreview={setLogoPreview}
          applying={applying} onClose={() => setShowApply(false)}
          onSubmit={handleApply}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium bg-dark-700 text-white border border-dark-600">
          {toast}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════════

function KPI({ icon: Icon, label, value, color }) {
  const colors = {
    amber:   'from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-300',
    emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20 text-emerald-300',
    violet:  'from-violet-500/10 to-purple-500/10 border-violet-500/20 text-violet-300',
    cyan:    'from-cyan-500/10 to-blue-500/10 border-cyan-500/20 text-cyan-300',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-3 border`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

function ChainPill({ active, onClick, icon, name, count, activeColor }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl text-xs font-semibold whitespace-nowrap transition-all border ${
        active
          ? `bg-gradient-to-br ${activeColor || 'from-amber-500/20 to-orange-500/20 border-amber-400/40 text-amber-200'}`
          : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50 hover:border-dark-600'
      }`}>
      <span>{icon}</span>
      <span>{name}</span>
      <span className={`text-[10px] px-1.5 py-px rounded ${active ? 'bg-white/10' : 'bg-dark-700 text-gray-500'}`}>
        {count}
      </span>
    </button>
  );
}

function SpotlightCard({ project, onClick }) {
  const chain = CHAINS.find(c => c.id === project.chain);
  return (
    <div onClick={onClick}
      className="cursor-pointer relative overflow-hidden rounded-2xl border border-amber-500/30 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10 group"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.10) 0%, rgba(16,16,20,1) 50%)' }}>
      <div className="h-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          {project.logo ? (
            <img src={project.logo} alt={project.projectName} className="w-14 h-14 rounded-2xl object-cover ring-2 ring-amber-400/30 flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold ring-2 ring-amber-400/30 flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.20), rgba(16,185,129,0.15))' }}>
              <span className="text-amber-300">{(project.tokenSymbol || '?')[0]}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="text-sm font-bold text-white truncate">{project.projectName}</h3>
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-xs font-bold text-amber-300">${project.tokenSymbol}</span>
              {chain && <span className="text-[10px] text-gray-500">{chain.icon} {chain.name}</span>}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-3 leading-relaxed line-clamp-2 min-h-[2.4rem]">
          {project.description || 'Verified project on haythix.'}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-500/15">
          <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Recently verified
          </span>
          <span className="text-[10px] text-gray-500">
            {project.verifiedAt ? new Date(project.verifiedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, onClick, isAdmin, onDelete }) {
  const chain = CHAINS.find(c => c.id === project.chain);
  return (
    <div onClick={onClick}
      className="cursor-pointer rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/5 group"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.06) 0%, rgba(16,16,20,1) 35%)', border: '1px solid rgba(245,158,11,0.12)' }}>
      <div className="h-0.5 bg-gradient-to-r from-amber-500/50 via-yellow-400/70 to-amber-500/50" />
      <div className="p-3.5">
        <div className="flex items-center gap-2.5">
          {project.logo ? (
            <img src={project.logo} alt={project.projectName} className="w-10 h-10 rounded-xl object-cover ring-1 ring-amber-500/20 flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0 ring-1 ring-amber-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(16,185,129,0.12))' }}>
              <span className="text-amber-400">{(project.tokenSymbol || '?')[0]}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <h3 className="text-sm font-bold text-white truncate">{project.projectName}</h3>
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] font-bold text-amber-400">${project.tokenSymbol}</span>
              {chain && <span className="text-[9px] text-gray-500">{chain.icon}</span>}
            </div>
          </div>
        </div>
        <p className="text-[10px] text-gray-500 mt-2 leading-relaxed line-clamp-2 min-h-[2rem]">
          {project.description || 'Verified project on haythix.'}
        </p>
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/5">
          {project.websiteUrl && (
            <a href={project.websiteUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
              className="text-[9px] text-gray-400 hover:text-white flex items-center gap-0.5 bg-white/5 px-1.5 py-0.5 rounded transition-all">
              <Globe className="w-2.5 h-2.5" /> Web
            </a>
          )}
          {project.twitterHandle && (
            <a href={`https://twitter.com/${project.twitterHandle}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
              className="text-[9px] text-gray-400 hover:text-white flex items-center gap-0.5 bg-white/5 px-1.5 py-0.5 rounded transition-all">
              <Twitter className="w-2.5 h-2.5" /> X
            </a>
          )}
          {project.activeCampaigns > 0 && (
            <span className="text-[9px] text-violet-300 bg-violet-500/10 px-1.5 py-0.5 rounded border border-violet-500/20 flex items-center gap-0.5">
              <TrendingUp className="w-2.5 h-2.5" /> {project.activeCampaigns}
            </span>
          )}
          <span className="text-[8px] text-gray-600 ml-auto">
            {project.verifiedAt ? new Date(project.verifiedAt).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : ''}
          </span>
        </div>
        {isAdmin && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="mt-1.5 text-[9px] text-gray-700 hover:text-red-400 transition-colors flex items-center gap-0.5">
            <Trash2 className="w-2.5 h-2.5" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

function PendingApplicationCard({ project, onApprove, onReject }) {
  return (
    <div className="bg-dark-800 rounded-2xl p-5 border border-amber-500/20">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {project.logo ? (
            <img src={project.logo} alt={project.projectName} className="w-12 h-12 rounded-xl object-cover ring-1 ring-amber-500/20 flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-dark-700 flex items-center justify-center text-amber-400 font-bold ring-1 ring-amber-500/20 flex-shrink-0">
              {(project.tokenSymbol || '?')[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              {project.projectName}
              <span className="text-[10px] font-bold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-full border border-amber-500/30">PENDING</span>
            </h3>
            <p className="text-xs text-amber-300 mt-0.5">
              ${project.tokenSymbol} · {project.chain} · {project.tokenAddress?.slice(0, 10)}...
            </p>
            {project.description && <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">{project.description}</p>}
            <div className="flex gap-3 mt-2 flex-wrap">
              {project.websiteUrl && (
                <a href={project.websiteUrl} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-gray-500 hover:text-white inline-flex items-center gap-1">
                  <Globe className="w-3 h-3" /> {project.websiteUrl}
                </a>
              )}
              {project.twitterHandle && (
                <a href={`https://twitter.com/${project.twitterHandle}`} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-gray-500 hover:text-white inline-flex items-center gap-1">
                  <Twitter className="w-3 h-3" /> @{project.twitterHandle}
                </a>
              )}
            </div>
            <p className="text-[9px] text-gray-600 mt-1.5">Applied {new Date(project.appliedAt).toLocaleString()}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={onApprove}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-all flex items-center gap-1 border border-emerald-500/30">
            <CheckCircle className="w-3.5 h-3.5" /> Approve
          </button>
          <button onClick={onReject}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-all flex items-center gap-1 border border-red-500/30">
            <X className="w-3.5 h-3.5" /> Reject
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectDetailModal({ project, onClose }) {
  const chain = CHAINS.find(c => c.id === project.chain);
  const addr = project.tokenAddress || '';
  const daysSinceVerified = project.verifiedAt
    ? Math.floor((Date.now() - new Date(project.verifiedAt).getTime()) / 86400000)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-amber-500/20 shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="h-1 bg-gradient-to-r from-amber-500/60 via-yellow-400/80 to-amber-500/60" />
        <div className="relative p-6 pb-4" style={{ background: 'linear-gradient(180deg, rgba(245,158,11,0.08) 0%, transparent 100%)' }}>
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 text-gray-500 hover:text-white hover:bg-dark-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            {project.logo ? (
              <img src={project.logo} alt={project.projectName} className="w-16 h-16 rounded-2xl object-cover ring-2 ring-amber-500/30" />
            ) : (
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold ring-2 ring-amber-500/30"
                style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(16,185,129,0.15))' }}>
                <span className="text-amber-300">{(project.tokenSymbol || '?')[0]}</span>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-white">{project.projectName}</h2>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Verified
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-base font-bold text-amber-300">${project.tokenSymbol}</span>
                {chain && <span className="text-xs text-gray-400">{chain.icon} {chain.name}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">About</p>
            <p className="text-sm text-gray-300 leading-relaxed">
              {project.description || 'Verified token project on the haythix platform.'}
            </p>
          </div>

          <div className="bg-dark-700/50 rounded-xl p-3 border border-dark-700/50">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Contract Address</p>
            <p className="text-xs text-white font-mono break-all">{addr || 'Not provided'}</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Stat label="Status" value="Active" valueClass="text-emerald-400" />
            <Stat label="Verified For" value={`${daysSinceVerified}d`} />
            <Stat label="Campaigns" value={project.activeCampaigns || 0} valueClass="text-amber-300" />
          </div>

          <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/15">
            <p className="text-[10px] text-emerald-400 uppercase tracking-wider mb-2 font-bold">Verification Details</p>
            <div className="space-y-1.5">
              <CheckRow text="Identity verified by haythix team" />
              <CheckRow text="Token contract reviewed for safety patterns" />
              <CheckRow text="Project legitimacy confirmed" />
              {project.activeCampaigns > 0 && (
                <CheckRow text="Marketing campaigns transparently disclosed" violet />
              )}
            </div>
          </div>

          {(project.teamSize || project.launchDate || project.auditUrl) && (
            <div className="grid grid-cols-3 gap-2">
              {project.teamSize && <Stat label="Team Size" value={project.teamSize} />}
              {project.launchDate && <Stat label="Launched" value={project.launchDate} />}
              {project.auditUrl && (
                <a href={project.auditUrl} target="_blank" rel="noopener noreferrer"
                  className="bg-emerald-500/5 rounded-xl p-2.5 text-center border border-emerald-500/15 hover:bg-emerald-500/10 transition-all">
                  <p className="text-[9px] text-emerald-400 uppercase tracking-wider">Audit</p>
                  <p className="text-xs font-bold text-emerald-400">View Report ↗</p>
                </a>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {[
              { url: project.websiteUrl, icon: '🌐', label: 'Website' },
              { url: project.twitterHandle ? `https://twitter.com/${project.twitterHandle}` : null, icon: '𝕏', label: project.twitterHandle ? `@${project.twitterHandle}` : null },
              { url: project.discordUrl, icon: '💬', label: 'Discord' },
              { url: project.telegramUrl, icon: '✈️', label: 'Telegram' },
              { url: project.linkedinUrl, icon: '💼', label: 'LinkedIn' },
              { url: project.githubUrl, icon: '🔧', label: 'GitHub' },
            ].filter(l => l.url).map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                className="text-center text-[10px] font-medium text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 py-2 rounded-lg border border-white/5 transition-all flex items-center justify-center gap-1">
                <span>{link.icon}</span> {link.label}
              </a>
            ))}
          </div>

          <p className="text-[9px] text-gray-600 text-center flex items-center justify-center gap-1">
            <Calendar className="w-3 h-3" />
            Verified on {project.verifiedAt ? new Date(project.verifiedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'} · Verification is not financial advice
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, valueClass = 'text-white' }) {
  return (
    <div className="bg-dark-700/30 rounded-xl p-2.5 text-center border border-dark-700/50">
      <p className="text-[9px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}

function CheckRow({ text, violet }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <CheckCircle className={`w-3.5 h-3.5 ${violet ? 'text-violet-400' : 'text-emerald-400'}`} />
      <span className={violet ? 'text-violet-200' : 'text-gray-300'}>{text}</span>
    </div>
  );
}

function ApplyModal({ form, setForm, logoPreview, setLogoPreview, applying, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-amber-500/20 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="h-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400" />
        <div className="p-5 border-b border-dark-700/50 flex items-center justify-between"
          style={{ background: 'linear-gradient(180deg, rgba(245,158,11,0.06) 0%, transparent 100%)' }}>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" /> Apply for Verification
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-4">
            <div className="relative">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-16 h-16 rounded-xl object-cover border border-dark-600" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-dark-700 border border-dark-600 flex items-center justify-center text-gray-600 text-xs">Logo</div>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Project Logo</label>
              <input type="file" accept="image/*" onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                const img = new Image();
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  canvas.width = 128; canvas.height = 128;
                  const ctx = canvas.getContext('2d');
                  const size = Math.min(img.width, img.height);
                  const sx = (img.width - size) / 2, sy = (img.height - size) / 2;
                  ctx.drawImage(img, sx, sy, size, size, 0, 0, 128, 128);
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                  setLogoPreview(dataUrl);
                  setForm(f => ({ ...f, logo: dataUrl }));
                };
                img.src = URL.createObjectURL(file);
              }} className="text-xs text-gray-400 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-dark-700 file:text-gray-300 hover:file:bg-dark-600" />
              <p className="text-[9px] text-gray-600 mt-0.5">Square image recommended, auto-resized to 128×128</p>
            </div>
          </div>

          <Field label="Project Name *" value={form.projectName} onChange={v => setForm({ ...form, projectName: v })} placeholder="e.g. Pepe" />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Token Symbol *" value={form.tokenSymbol} onChange={v => setForm({ ...form, tokenSymbol: v.toUpperCase() })} placeholder="PEPE" maxLength={10} />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Chain *</label>
              <select value={form.chain} onChange={e => setForm({ ...form, chain: e.target.value })}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:border-amber-400 focus:outline-none">
                {CHAINS.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
          </div>

          <Field label="Token Address *" value={form.tokenAddress} onChange={v => setForm({ ...form, tokenAddress: v })} placeholder="0x..." mono />

          <div>
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of your project..." maxLength={500} rows={2}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-amber-400 focus:outline-none resize-none" />
          </div>

          <p className="text-[10px] text-amber-300 font-bold uppercase tracking-wider mt-1">Social & Community Links</p>
          <div className="grid grid-cols-2 gap-2">
            <SmallInput value={form.websiteUrl}    onChange={v => setForm({ ...form, websiteUrl: v })}    placeholder="🌐 Website URL" />
            <SmallInput value={form.twitterHandle} onChange={v => setForm({ ...form, twitterHandle: v.replace('@', '') })} placeholder="𝕏 Twitter username" />
            <SmallInput value={form.discordUrl}    onChange={v => setForm({ ...form, discordUrl: v })}    placeholder="💬 Discord URL" />
            <SmallInput value={form.telegramUrl}   onChange={v => setForm({ ...form, telegramUrl: v })}   placeholder="✈️ Telegram URL" />
            <SmallInput value={form.linkedinUrl}   onChange={v => setForm({ ...form, linkedinUrl: v })}   placeholder="💼 LinkedIn URL" />
            <SmallInput value={form.githubUrl}     onChange={v => setForm({ ...form, githubUrl: v })}     placeholder="🔧 GitHub URL" />
          </div>

          <p className="text-[10px] text-amber-300 font-bold uppercase tracking-wider mt-1">Project Details</p>
          <div className="grid grid-cols-3 gap-2">
            <SmallInput value={form.teamSize}   onChange={v => setForm({ ...form, teamSize: v })}   placeholder="Team size" />
            <SmallInput value={form.launchDate} onChange={v => setForm({ ...form, launchDate: v })} placeholder="Launch date" />
            <SmallInput value={form.auditUrl}   onChange={v => setForm({ ...form, auditUrl: v })}   placeholder="Audit URL" />
          </div>

          <button onClick={onSubmit} disabled={applying || !form.tokenSymbol || !form.tokenAddress || !form.projectName}
            className="w-full py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-dark-900 hover:from-amber-300 hover:to-orange-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Submit Application
          </button>
          <p className="text-[10px] text-gray-600 text-center">Applications are reviewed within 48 hours by the haythix team.</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, maxLength, mono }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength}
        className={`w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-amber-400 focus:outline-none ${mono ? 'font-mono' : ''}`} />
    </div>
  );
}

function SmallInput({ value, onChange, placeholder }) {
  return (
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-xs placeholder-gray-600 focus:border-amber-400 focus:outline-none" />
  );
}
